import React, { useMemo } from 'react'
import { NavigateOptions, RouteObject, useNavigate } from 'react-router-dom'

import { DuplicateRouteAliasError, ReservedRouteAliasError } from 'utils/routeLink/errors'
import { SuspensePage } from 'utils/router/components/SuspensePage'

import { makeRoutePath } from './makeRoutePath'
import { FlattenTree, MakeObjectPath, PatchedRouteObject } from './types'

/** Aliases taken by typedRouting.useNavigate() helpers, routes can't use them. */
const RESERVED_ROUTE_ALIASES: readonly string[] = ['back']

export const makeRouteObjects = (
  routes?: readonly PatchedRouteObject[],
): RouteObject[] | undefined => {
  if (!routes?.length) {
    return undefined
  }

  return routes.map((route) => {
    const { children, ...rest } = route
    if ('suspense' in rest && rest.lazy) {
      const importer = rest.lazy
      const alias = 'alias' in rest ? rest.alias : undefined

      delete rest.lazy

      rest.element = <SuspensePage alias={alias} importer={importer} />
    }

    if (rest.index) {
      return rest
    }

    if ('query' in rest) {
      delete rest.query
    }

    if ('alias' in rest) {
      delete rest.alias
    }

    const madeChildren = makeRouteObjects(children)

    if (madeChildren) {
      return {
        ...rest,
        children: madeChildren,
      }
    }

    return { ...rest }
  })
}

export const makeRoutePathObjects = <T extends readonly PatchedRouteObject[]>(
  routes: T,
  parentPath?: string,
): MakeObjectPath<FlattenTree<T>> => {
  const aliases = new Set<string>()

  const result = {} as MakeObjectPath<FlattenTree<T>>

  for (const route of routes) {
    if (route.index) continue

    const { children, query, path, alias } = route
    const fullPath = parentPath && parentPath !== '/' ? `${parentPath}/${path}` : path

    if (alias) {
      if (RESERVED_ROUTE_ALIASES.includes(alias)) {
        throw new ReservedRouteAliasError(alias, fullPath)
      }

      if (aliases.has(alias)) {
        throw new DuplicateRouteAliasError(alias, fullPath)
      }

      aliases.add(alias)
      Object.assign(result, {
        [alias]: { ...makeRoutePath({ path: path === '/' ? '' : fullPath, query }), alias },
      })
    }

    if (children) {
      Object.assign(result, makeRoutePathObjects(children, fullPath))
    }
  }

  return result
}

export const makeRoutes = <T extends readonly PatchedRouteObject[]>(tree: T) => ({
  routeObjects: makeRouteObjects(tree) || [],
  routes: makeRoutePathObjects(tree),
})

type MakeRoutesGeneric = Record<
  string,
  { useTypedParams: () => unknown; getLink: (params?: TSFixMe) => string }
>

export type MakeUseTypedNavigateResult<T extends MakeRoutesGeneric> = {
  [K in keyof T]: undefined extends Parameters<T[K]['getLink']>[0]
    ? (params?: Parameters<T[K]['getLink']>[0], options?: NavigateOptions) => void
    : (params: Parameters<T[K]['getLink']>[0], options?: NavigateOptions) => void
} & {
  /** Goes one entry back in the history, use it instead of navigate(-1). */
  back: () => void
}

export const makeUseTypedNavigate =
  <T extends MakeRoutesGeneric>(routes: T) =>
  () => {
    const navigate = useNavigate()
    const result = useMemo(
      () =>
        ({
          ...Object.fromEntries(
            Object.keys(routes).map((key) => [
              key,
              (params: unknown, options?: NavigateOptions) =>
                options
                  ? navigate(routes[key].getLink(params), options)
                  : navigate(routes[key].getLink(params)),
            ]),
          ),
          back: () => navigate(-1),
        }) as MakeUseTypedNavigateResult<T>,
      [navigate],
    )

    return result
  }

export type MakeUseTypedParamsResult<T extends MakeRoutesGeneric> = {
  [K in keyof T]: T[K]['useTypedParams']
}

export const makeUseTypedParams = <T extends MakeRoutesGeneric>(routes: T) =>
  Object.fromEntries(
    Object.keys(routes).map((key) => [key, () => routes[key].useTypedParams()]),
  ) as MakeUseTypedParamsResult<T>

export type MakeRouteLinkBuilderResult<T extends MakeRoutesGeneric> = {
  [K in keyof T]: T[K]['getLink']
}

export const makeRouteLinkBuilder = <T extends MakeRoutesGeneric>(routes: T) =>
  Object.fromEntries(
    Object.keys(routes).map((key) => [key, routes[key].getLink]),
  ) as MakeRouteLinkBuilderResult<T>
